import { useEffect, useState, useCallback } from 'react';
import { UserPlus, Shield, Trash2, Mail, Layers } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Alert } from '../components/Alert';
import { Loader } from '../components/Loader';
import { Dialog } from '../components/Dialog';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { InvitationPublic, MemberListItem, MembershipRole } from '../types';

type ProductOption = { code: string; name: string };

const RESTRICTED_ROLES: MembershipRole[] = ['member', 'readonly'];
const isRestricted = (role: MembershipRole) => RESTRICTED_ROLES.includes(role);

export function MembersPage() {
  const { activeOrg, activeRole, session } = useAuth();
  const [members, setMembers] = useState<MemberListItem[]>([]);
  const [invitations, setInvitations] = useState<InvitationPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Invite modal state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<MembershipRole>('member');
  const [inviteAllowedProducts, setInviteAllowedProducts] = useState<string[]>([]);
  const [inviteLocale, setInviteLocale] = useState<'fr' | 'en'>('fr');
  const [inviting, setInviting] = useState(false);
  // Shown once after creating an invitation. There is no email delivery yet, so
  // the link has to be copied out by hand or the invitation is unusable.
  const [issuedLink, setIssuedLink] = useState<string | null>(null);

  // Member app edit state. `editRole` is set when the dialog was opened by a
  // role change, so the role and its apps are saved together.
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [editAppsMember, setEditAppsMember] = useState<MemberListItem | null>(null);
  const [editRole, setEditRole] = useState<MembershipRole | null>(null);
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [savingApps, setSavingApps] = useState(false);
  const productName = (code: string) => products.find((p) => p.code === code)?.name || code;

  // Role edit / Revoke state
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);

  const isManager = activeRole === 'owner' || activeRole === 'admin';

  const loadMembers = useCallback(async () => {
    if (!activeOrg?.id) return;
    // The roster, invitations and full product list are management-only;
    // everyone else would just get 403s.
    if (!isManager) {
      setLoading(false);
      return;
    }
    try {
      const [data, invites, catalogue] = await Promise.all([
        api.orgs.listMembers(activeOrg.id),
        api.orgs.listInvitations(activeOrg.id),
        api.orgs.catalogue(activeOrg.id),
      ]);
      setMembers(data);
      setInvitations(invites);
      setProducts(catalogue.products.map((p) => ({ code: p.code, name: p.name })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, [activeOrg?.id, isManager]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrg?.id || !inviteEmail.trim()) return;
    setInviting(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const created = await api.orgs.inviteMember(activeOrg.id, {
        email: inviteEmail.trim(),
        role: inviteRole,
        allowed_products: isRestricted(inviteRole) ? inviteAllowedProducts : undefined,
        locale: inviteLocale,
      });
      // Only surface the raw link when delivery failed. If the email went out,
      // showing the token needlessly puts a live credential on screen.
      setIssuedLink(created.email_sent ? null : created.accept_url);
      setSuccessMsg(
        created.email_sent
          ? `Invitation sent to ${created.email} as ${created.role}.`
          : `Invitation created for ${created.email} as ${created.role}, but the email could not be sent.`
      );
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      setInviteAllowedProducts([]);
      await loadMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite member');
    } finally {
      setInviting(false);
    }
  };

  const handleOpenEditApps = (member: MemberListItem, newRole: MembershipRole | null = null) => {
    setEditAppsMember(member);
    setEditRole(newRole);
    setSelectedApps(member.allowed_products || []);
  };

  const closeEditApps = () => {
    setEditAppsMember(null);
    setEditRole(null);
  };

  const handleSaveApps = async () => {
    if (!activeOrg?.id || !editAppsMember) return;
    setSavingApps(true);
    setError(null);
    const role = editRole || editAppsMember.role;
    try {
      await api.orgs.updateMemberRole(activeOrg.id, editAppsMember.membership_id, {
        role,
        allowed_products: selectedApps,
      });
      const who = editAppsMember.email || editAppsMember.display_name;
      setSuccessMsg(editRole ? `${who} is now ${role}, with access to the selected applications.`
                             : `Updated assigned applications for ${who}.`);
      closeEditApps();
      await loadMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update assigned applications');
    } finally {
      setSavingApps(false);
    }
  };

  const handleRevokeInvitation = async (invitation: InvitationPublic) => {
    if (!activeOrg?.id) return;
    if (!window.confirm(`Revoke the invitation sent to ${invitation.email}?`)) return;
    setError(null);
    try {
      await api.orgs.revokeInvitation(activeOrg.id, invitation.id);
      setSuccessMsg(`Invitation for ${invitation.email} revoked.`);
      await loadMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke invitation');
    }
  };

  const handleRoleChange = async (member: MemberListItem, newRole: MembershipRole) => {
    if (!activeOrg?.id) return;
    // Moving into a restricted role decides what they can open: ask, don't guess.
    if (isRestricted(newRole) && !isRestricted(member.role)) {
      handleOpenEditApps(member, newRole);
      return;
    }
    setUpdatingRole(member.membership_id);
    setError(null);
    try {
      await api.orgs.updateMemberRole(activeOrg.id, member.membership_id, { role: newRole });
      await loadMembers();
      setSuccessMsg(`Role updated for ${member.email || member.display_name} to ${newRole}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleRevoke = async (member: MemberListItem) => {
    if (!activeOrg?.id) return;
    if (!window.confirm(`Revoke membership for ${member.email || member.display_name}?`)) return;

    setError(null);
    try {
      await api.orgs.revokeMember(activeOrg.id, member.membership_id);
      await loadMembers();
      setSuccessMsg(`Revoked membership for ${member.email || member.display_name}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke member');
    }
  };

  if (loading) {
    return <Loader label="Loading members..." />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--fw-bold)' }}>Team & Permissions</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
            Control user access roles and active invitations within organisation <strong style={{ color: 'var(--text-primary)' }}>{activeOrg?.display_name}</strong>.
          </p>
        </div>

        {isManager && (
          <Button variant="primary" size="sm" onClick={() => setInviteOpen(true)} icon={<UserPlus size={14} />}>
            Invite Member
          </Button>
        )}
      </div>

      {error && (
        <Alert tone="danger" title="Error">
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert tone="success" title="Success">
          {successMsg}
        </Alert>
      )}

      {issuedLink && (
        <Alert tone="warning" title="Email not sent — copy this invitation link now">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span>
              The invitation is valid, but the email could not be delivered. This link is
              shown once and cannot be retrieved again — only a hash of it is stored. Send
              it to the invitee yourself, or revoke the invitation and try again.
            </span>
            <code
              style={{
                display: 'block',
                padding: '8px 10px',
                background: 'var(--bg-inset)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--text-xs)',
                wordBreak: 'break-all',
                color: 'var(--text-primary)',
              }}
            >
              {issuedLink}
            </code>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigator.clipboard?.writeText(issuedLink)}
              >
                Copy link
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setIssuedLink(null)}>
                Dismiss
              </Button>
            </div>
          </div>
        </Alert>
      )}

      <Card padding="0">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 'var(--text-sm)' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-inset)' }}>
                <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>User</th>
                <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Role</th>
                <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Allowed Apps</th>
                <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Joined / Invited</th>
                <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                const isMe = member.principal_id === session?.principal.id;
                const isUpdating = updatingRole === member.membership_id;

                return (
                  <tr key={member.membership_id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 'var(--radius-full)',
                            background: 'var(--navy-700)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--accent)',
                            fontWeight: 600,
                            fontSize: 'var(--text-xs)',
                            flexShrink: 0,
                          }}
                        >
                          {member.display_name?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div style={{ fontWeight: 'var(--fw-medium)', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            {member.display_name || member.email || 'Pending User'}
                            {isMe && <Badge tone="accent">You</Badge>}
                          </div>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                            {member.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: '14px 16px' }}>
                      {isManager && !isMe ? (
                        <select
                          value={member.role}
                          disabled={isUpdating}
                          onChange={(e) => handleRoleChange(member, e.target.value as MembershipRole)}
                          style={{
                            background: 'var(--bg-inset)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border-default)',
                            borderRadius: 'var(--radius-sm)',
                            padding: '4px 8px',
                            fontSize: 'var(--text-xs)',
                            cursor: 'pointer',
                          }}
                        >
                          <option value="owner">Owner</option>
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                          <option value="billing">Billing</option>
                          <option value="readonly">Readonly</option>
                        </select>
                      ) : (
                        <Badge tone={member.role === 'owner' ? 'accent' : 'neutral'}>
                          {member.role.toUpperCase()}
                        </Badge>
                      )}
                    </td>

                    <td style={{ padding: '14px 16px' }}>
                      {!isRestricted(member.role) ? (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>All organisation apps</span>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {(!member.allowed_products || member.allowed_products.length === 0) ? (
                            <Badge tone="neutral">None (No access)</Badge>
                          ) : (
                            member.allowed_products.map((p) => (
                              <Badge key={p} tone="accent">
                                {productName(p)}
                              </Badge>
                            ))
                          )}
                          {isManager && !isMe && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenEditApps(member)}
                              style={{ padding: '2px 6px', fontSize: '11px', height: 'auto' }}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                    </td>

                    <td style={{ padding: '14px 16px' }}>
                      <Badge
                        tone={member.status === 'active' ? 'success' : member.status === 'invited' ? 'warning' : 'danger'}
                        dot
                      >
                        {member.status}
                      </Badge>
                    </td>

                    <td style={{ padding: '14px 16px', color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
                      {member.accepted_at ? (
                        <span>Accepted {new Date(member.accepted_at).toLocaleDateString()}</span>
                      ) : member.invited_at ? (
                        <span>Invited {new Date(member.invited_at).toLocaleDateString()}</span>
                      ) : (
                        <span>—</span>
                      )}
                    </td>

                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      {isManager && !isMe && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRevoke(member)}
                          icon={<Trash2 size={13} />}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pending invitations. Separate from members on purpose: an invitee has
          no principal and no membership until they accept. */}
      {isManager && invitations.length > 0 && (
        <Card padding="0">
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
            <h2 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--fw-semibold)' }}>
              Pending invitations
            </h2>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: 2 }}>
              These people have been invited but have not signed in yet. They become
              members only once they accept.
            </p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 'var(--text-sm)' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-inset)' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Email</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Role</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Allowed Apps</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Expires</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((invitation) => (
                  <tr key={invitation.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '14px 16px', color: 'var(--text-primary)' }}>{invitation.email}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <Badge tone={invitation.role === 'owner' ? 'accent' : 'neutral'}>
                        {invitation.role.toUpperCase()}
                      </Badge>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      {!isRestricted(invitation.role) ? (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>All organisation apps</span>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {(!invitation.allowed_products || invitation.allowed_products.length === 0) ? (
                            <Badge tone="neutral">None (No access)</Badge>
                          ) : (
                            invitation.allowed_products.map((p) => (
                              <Badge key={p} tone="accent">
                                {productName(p)}
                              </Badge>
                            ))
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
                      {new Date(invitation.expires_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRevokeInvitation(invitation)}
                        icon={<Trash2 size={13} />}
                      >
                        Revoke
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Role explanation summary */}
      <Card padding="var(--space-4)" style={{ background: 'var(--bg-inset)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Shield size={18} color="var(--accent)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--text-primary)' }}>Role Permissions Matrix:</strong><br />
            • <strong>Owner:</strong> Full control, billing, destructive actions, membership management (must have at least 1).<br />
            • <strong>Admin:</strong> Technical configuration, product activation, member invites.<br />
            • <strong>Billing:</strong> Access to billing, invoice receipts, VAT/tax identifiers.<br />
            • <strong>Member / Readonly:</strong> Only the applications assigned to them; no billing, members or audit log.
          </div>
        </div>
      </Card>

      {/* Invite Member Dialog */}
      <Dialog
        open={inviteOpen}
        title="Invite Team Member"
        onClose={() => setInviteOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setInviteOpen(false)} disabled={inviting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              icon={<Mail size={14} />}
            >
              {inviting ? 'Inviting...' : 'Send Invitation'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Input
            label="User Email Address *"
            type="email"
            placeholder="colleague@acme.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            disabled={inviting}
            required
            hint="An invitation record will be tied to this email in the organisation."
          />

          <Select
            label="Initial Role *"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as MembershipRole)}
            disabled={inviting}
            options={[
              { label: 'Member - Standard product user', value: 'member' },
              { label: 'Admin - Can manage products and invite members', value: 'admin' },
              { label: 'Owner - Full administrative and billing authority', value: 'owner' },
              { label: 'Billing - Dedicated invoice & tax manager', value: 'billing' },
              { label: 'Readonly - Read-only dashboard viewer', value: 'readonly' },
            ]}
          />

          {isRestricted(inviteRole) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--fw-medium)', color: 'var(--text-secondary)' }}>
                Assigned Applications
              </label>
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: 4 }}>
                Select which applications this member is allowed to see and launch. If none are selected, the member has access to nothing.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', background: 'var(--bg-inset)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)' }}>
                {products.map((prod) => {
                  const checked = inviteAllowedProducts.includes(prod.code);
                  return (
                    <label key={prod.code} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--text-sm)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setInviteAllowedProducts([...inviteAllowedProducts, prod.code]);
                          } else {
                            setInviteAllowedProducts(inviteAllowedProducts.filter((c) => c !== prod.code));
                          }
                        }}
                      />
                      <span>{prod.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <Select
            label="Email language"
            value={inviteLocale}
            onChange={(e) => setInviteLocale(e.target.value as 'fr' | 'en')}
            disabled={inviting}
            hint="Used if this person has no CYRN account yet; otherwise their own language is used."
            options={[
              { label: 'Français', value: 'fr' },
              { label: 'English', value: 'en' },
            ]}
          />
        </form>
      </Dialog>

      {/* Edit Assigned Apps Dialog */}
      <Dialog
        open={!!editAppsMember}
        title={`${editRole ? `Change role to ${editRole}` : 'Edit Application Access'}: ${editAppsMember?.email || editAppsMember?.display_name || 'Member'}`}
        onClose={closeEditApps}
        footer={
          <>
            <Button variant="secondary" onClick={closeEditApps} disabled={savingApps}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveApps}
              disabled={savingApps}
              icon={<Layers size={14} />}
            >
              {savingApps ? 'Saving...' : editRole ? 'Change Role' : 'Save Permissions'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
            {editRole
              ? `As ${editRole}, this person will only see and open the applications you select here. `
              : 'Normal members can only view and launch applications explicitly assigned to them. '}
            Leaving all unchecked means they will see and access no applications.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px', background: 'var(--bg-inset)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)' }}>
            {products.map((prod) => {
              const checked = selectedApps.includes(prod.code);
              return (
                <label key={prod.code} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 'var(--text-sm)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedApps([...selectedApps, prod.code]);
                      } else {
                        setSelectedApps(selectedApps.filter((c) => c !== prod.code));
                      }
                    }}
                  />
                  <span>{prod.name} (<code>{prod.code}</code>)</span>
                </label>
              );
            })}
          </div>
        </div>
      </Dialog>
    </div>
  );
}
